import React, { useCallback, useEffect, useState } from 'react';
import booksCloseAPI from './services/booksCloseApi';
import { formatApiError } from '../../utils/errorMessage';

const ACTION = {
  close: 'Closed the books', move_forward: 'Moved the closing date forward', move_back: 'Moved the closing date back',
  reopen: 'Reopened the books', password_set: 'Set the closing-date password', password_change: 'Changed the closing-date password',
  password_reset: 'Reset the closing-date password', password_remove: 'Removed the closing-date password',
  password_failed: 'Wrong closing-date password', override_granted: 'Unlocked edits in closed books',
  override_edit: 'Edited inside closed books', delegate_add: 'Allowed someone to close the books', delegate_remove: 'Removed someone from closing the books'
};
const monthLabel = (m) => new Date(`${m}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
const dayLabel = (d) => (d ? new Date(`${d}T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '');

// Accounts that block closing: need reconciliation, have open months, not excluded with a reason.
export function blockingAccounts(checklist, exclusions) {
  return (checklist || []).filter((a) => !a.ready && !(exclusions[a.account_id] || '').trim());
}

// Business closing date: status, checklist, close/move/reopen, unlock, password.
// initialData: first render before the load finishes (also used by tests).
export default function BooksClosePanel({ businessId, onClosedChange, initialData = null }) {
  const [data, setData] = useState(initialData);
  const [error, setError] = useState('');
  const [through, setThrough] = useState('');
  const [attest, setAttest] = useState(false);
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [exclusions, setExclusions] = useState({});
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ login: '', next: '', reset: false });

  const load = useCallback(async (day) => {
    try {
      const r = await booksCloseAPI.overview(businessId, day);
      setData(r.data);
      setError('');
      if (onClosedChange) onClosedChange(r.data.closed_through);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [businessId, onClosedChange]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (through) load(through); }, [through, load]);

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      setPassword(''); setReason(''); setAttest(false);
      await load(through || undefined);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return error ? <p className="text-red-600" role="alert">{error}</p> : null;
  const closed = data.closed_through;
  const forward = through && (!closed || through > closed);
  const back = through && closed && through < closed;
  const blocking = forward ? blockingAccounts(data.checklist, exclusions) : [];
  const exclusionList = Object.entries(exclusions).filter(([, r]) => (r || '').trim()).map(([id, r]) => ({ account_id: Number(id), reason: r.trim() }));

  return (
    <section className="bg-white rounded shadow p-3 space-y-3" aria-label="Close the books">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold">Close the books</h2>
        <span data-testid="closed-through" className={closed ? 'px-2 py-1 rounded bg-gray-800 text-white text-sm' : 'text-sm text-gray-600'}>
          {closed ? `Books closed through ${dayLabel(closed)}` : 'Books are open'}
        </span>
        {data.override_until && <span className="text-sm text-amber-700">Edits in closed books unlocked until {new Date(data.override_until).toLocaleTimeString()}</span>}
      </div>
      {error && <p className="text-red-600" role="alert">{error}</p>}

      {data.checklist.length > 0 && (
        <div>
          <p className="text-sm text-gray-700">Reconciliation through {dayLabel(data.checklist_through)}</p>
          <table className="text-sm min-w-full" data-testid="books-checklist">
            <tbody>
              {data.checklist.map((a) => (
                <tr key={a.account_id} className="border-t align-top">
                  <td className="py-1 pr-3">
                    <span className={a.ready ? 'text-green-700' : 'text-red-700 font-semibold'}>{a.ready ? '\u2713' : '!'}</span>{' '}
                    {a.account_name}
                    {!a.needs_reconciliation && <span className="text-xs text-gray-500"> (no reconciliation needed)</span>}
                  </td>
                  <td className="py-1">
                    <div className="flex flex-wrap gap-1">
                      {a.months.map((m) => (
                        <span key={m.month} title={m.status} className={`px-1 rounded text-xs ${m.status === 'open' ? 'bg-yellow-100 text-red-700 font-bold' : 'bg-green-100 text-green-800'}`}>
                          {monthLabel(m.month)}
                        </span>
                      ))}
                    </div>
                    {forward && !a.ready && data.can_manage && (
                      <input aria-label={`Reason to exclude ${a.account_name}`} placeholder="Exclude with a reason" className="mt-1 px-2 py-1 border rounded w-full"
                        value={exclusions[a.account_id] || ''} onChange={(e) => setExclusions({ ...exclusions, [a.account_id]: e.target.value })} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.can_manage ? (
        <div className="space-y-2 border-t pt-2">
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm">Close the books through{' '}
              <input type="date" aria-label="Closing date" className="px-2 py-1 border rounded" value={through} onChange={(e) => setThrough(e.target.value)} />
            </label>
            {data.password_set && (
              <input type="password" aria-label="Closing-date password" placeholder="Closing-date password" autoComplete="off" className="px-2 py-1 border rounded"
                value={password} onChange={(e) => setPassword(e.target.value)} />
            )}
          </div>
          {forward && (
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} />
              I confirm everything up to {dayLabel(through)} is accurate and complete.
            </label>
          )}
          {(back || closed) && (
            <input aria-label="Reason" placeholder="Reason (needed to move back or reopen)" className="px-2 py-1 border rounded w-full" value={reason} onChange={(e) => setReason(e.target.value)} />
          )}
          <div className="flex flex-wrap gap-2">
            <button type="button" className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50" disabled={busy || !through || (forward && (!attest || blocking.length > 0)) || (back && !reason.trim())}
              onClick={() => run(() => booksCloseAPI.close(businessId, { through, attest, exclusions: exclusionList, reason, password }))}>
              {back ? 'Move closing date back' : 'Close the books'}
            </button>
            {closed && (
              <button type="button" className="px-3 py-1 rounded border disabled:opacity-50" disabled={busy || !reason.trim()}
                onClick={() => run(() => booksCloseAPI.reopen(businessId, { reason, password }))}>Reopen the books</button>
            )}
            {closed && data.password_set && (
              <button type="button" className="px-3 py-1 rounded border disabled:opacity-50" disabled={busy || !reason.trim() || !password}
                onClick={() => run(() => booksCloseAPI.unlock(businessId, { reason, password }))}>Unlock edits for 15 minutes</button>
            )}
          </div>
          {forward && blocking.length > 0 && <p className="text-sm text-red-700">Reconcile or exclude: {blocking.map((a) => a.account_name).join(', ')}</p>}
        </div>
      ) : (
        <p className="text-sm text-gray-600">Only the business owner, or someone the owner allowed, can change the closing date.</p>
      )}

      {data.is_owner && (
        <details className="border-t pt-2">
          <summary className="cursor-pointer text-sm font-medium">Settings: closing-date password</summary>
          <p className="text-xs text-gray-600 my-1">Optional. When set, it is needed to change the closing date or edit inside closed books. Enter your login password to set, change, reset or remove it.</p>
          <div className="flex flex-wrap gap-2 items-center">
            <input type="password" aria-label="Your login password" placeholder="Your login password" autoComplete="current-password" className="px-2 py-1 border rounded"
              value={pw.login} onChange={(e) => setPw({ ...pw, login: e.target.value })} />
            <input type="password" aria-label="New closing-date password" placeholder="New closing-date password" autoComplete="new-password" className="px-2 py-1 border rounded"
              value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
            {data.password_set && (
              <label className="text-sm"><input type="checkbox" checked={pw.reset} onChange={(e) => setPw({ ...pw, reset: e.target.checked })} /> I forgot the old one (reset)</label>
            )}
            <button type="button" className="px-3 py-1 rounded border disabled:opacity-50" disabled={busy || !pw.login || !pw.next}
              onClick={() => run(async () => { await booksCloseAPI.setPassword(businessId, { login_password: pw.login, new_password: pw.next, mode: pw.reset ? 'reset' : undefined }); setPw({ login: '', next: '', reset: false }); })}>
              {data.password_set ? 'Save new password' : 'Set password'}
            </button>
            {data.password_set && (
              <button type="button" className="px-3 py-1 rounded border disabled:opacity-50" disabled={busy || !pw.login}
                onClick={() => run(async () => { await booksCloseAPI.setPassword(businessId, { login_password: pw.login, new_password: null }); setPw({ login: '', next: '', reset: false }); })}>
                Remove password
              </button>
            )}
          </div>
        </details>
      )}

      {data.events.length > 0 && (
        <details className="border-t pt-2">
          <summary className="cursor-pointer text-sm font-medium">History</summary>
          <ul className="text-sm mt-1 space-y-1" data-testid="books-history">
            {data.events.map((e) => (
              <li key={e.id}>
                {new Date(e.created_at).toLocaleString()} - {ACTION[e.action] || e.action}
                {e.by_name ? ` by ${e.by_name}` : ''}
                {(e.old_date || e.new_date) ? ` (${dayLabel(e.old_date) || 'open'} to ${dayLabel(e.new_date) || 'open'})` : ''}
                {e.reason ? `: ${e.reason}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}